/**
 * SurfaceNet meshing algorithm for voxel terrain
 * 
 * Based on Mikola Lysenko's implementation:
 * https://github.com/mikolalysenko/isosurface
 * 
 * The MIT License (MIT)
 * Copyright (c) 2012-2013 Mikola Lysenko
 * 
 * SurfaceNets work by:
 * 1. For each cell (2×2×2 voxels), check if surface crosses (sign change in weights)
 * 2. If crossing, place a vertex at the weighted average of edge crossings
 * 3. Connect vertices with quads where edges cross the surface
 * 
 * This is a PURE function - it takes voxel data and produces mesh data.
 * It has no knowledge of Chunks or any game-specific concepts.
 * 
 * OPTIMIZATION: Single-pass mesh generation with face binning by material type.
 * Generates one vertex set, but separates faces into solid/transparent buckets.
 */

import {
  GRID_SIZE,
  CHUNK_SIZE,
  MATERIAL_TYPE_LUT,
  MAT_TYPE_SOLID,
  WEIGHT_SHIFT,
  WEIGHT_MASK,
  WEIGHT_MIN,
  MATERIAL_SHIFT,
  MATERIAL_MASK,
  LIGHT_MASK,
  LIGHT_MAX,
  BLOCK_LIGHT_SHIFT,
  BLOCK_LIGHT_MASK,
  BLOCK_LIGHT_MAX,
  INV_WEIGHT_MAX_PACKED,
  hasSurfaceCrossing,
} from '@worldify/shared';

// ============== Constants ==============

/** Mesh type indices for array-based mesh slots */
const MESH_SOLID = 0;
const MESH_TRANS = 1;
const MESH_LIQUID = 2;
const MESH_COUNT = 3;

// Grid dimension (CHUNK_SIZE + MESH_MARGIN) comes from shared constants — one derivation.
const GRID_STRIDE_X = GRID_SIZE;
const GRID_STRIDE_XY = GRID_SIZE * GRID_SIZE;

// ============== Types ==============

export interface SurfaceNetOutput {
  /** Vertex positions (x, y, z per vertex) */
  positions: Float32Array;
  /** Vertex normals (x, y, z per vertex) */
  normals: Float32Array;
  /** Triangle indices */
  indices: Uint32Array;
  /** Material ID per vertex */
  materials: Uint8Array;
  /** Sky light level per vertex (0.0-1.0, max of non-solid corners in 2x2x2 cell) */
  lights: Float32Array;
  /** Block light level per vertex (0.0-1.0, max of non-solid corners in 2x2x2 cell) */
  blockLights: Float32Array;
  /**
   * How much of this vertex's placement came from crossings into FILLED OPAQUE geometry (0..1) —
   * 0 out in the open, high on the rim where the layer's sheet welds into a solid.
   *
   * It exists for the SHADERS. Both non-solid layers displace their vertices (water waves, wind on
   * foliage), and displacing a welded rim lifts it off the surface it was welded to: the sheet is
   * drawn for where the material WAS, so the ground it uncovers has no sheet over it. The gap is
   * `amplitude / gradient` wide, which is nothing on a steep bank and a metre on a shallow one. A
   * vertex that touches opaque geometry is the seam, so the shader pins it and lets the open surface
   * move — waves flattening at the shore, which is what shallow water does anyway.
   */
  shoreFade: Float32Array;
  /**
   * Per-vertex grid cell index (baseIdx = z*GRID² + y*GRID + x of the cell's origin corner in the
   * GRID_SIZE³ grid). Lets light be RE-SAMPLED from updated voxel light without re-running
   * SurfaceNets — the relight path re-reads this cell's 8 corners. Fits Uint16 while GRID_SIZE³ < 65536.
   */
  cellIndices: Uint16Array;
  /**
   * Per-vertex 6-bit boundary flag: which chunk-cell planes the vertex sits on.
   * bit 0 = -X (cell x=0), 1 = +X (cell x=32), 2 = -Y, 3 = +Y, 4 = -Z, 5 = +Z.
   * Used by seam-normal reconciliation to find vertices coincident with a neighbor
   * chunk's boundary vertices.
   */
  boundaryFlags: Uint8Array;
  /** Number of vertices */
  vertexCount: number;
  /** Number of triangles */
  triangleCount: number;
}

/**
 * Combined output with separate solid, transparent, and liquid meshes.
 * Generated in a single pass for efficiency.
 */
export interface SplitSurfaceNetOutput {
  /** Mesh for solid (opaque) materials */
  solid: SurfaceNetOutput;
  /** Mesh for transparent materials */
  transparent: SurfaceNetOutput;
  /** Mesh for liquid materials */
  liquid: SurfaceNetOutput;
}

/**
 * Input data for surface net meshing.
 * Uses flat data array for cache-efficient direct access.
 */
export interface SurfaceNetInput {
  /** Dimensions of the voxel grid [x, y, z] */
  dims: [number, number, number];
  /** Flat packed voxel data array (dims[0] * dims[1] * dims[2]) */
  data: Uint32Array;
  /** 
   * Skip faces at high boundary for each axis [+X, +Y, +Z].
   * When true, faces at dims[axis]-2 are skipped (no neighbor to stitch with).
   */
  skipHighBoundary?: [boolean, boolean, boolean];
  /** LOD of the chunk; only the wrap's burial cap reads it. Absent = 0. */
  lod?: number;
}

// ============== Pre-computed Tables ==============

// All 24 pairs of cells in 2x2x2 grid (12 edges, 2 indices each)
const CUBE_EDGES = new Int32Array(24);

// Edge table: 256 possible cube configurations -> 12-bit edge crossing mask
const EDGE_TABLE = new Int32Array(256);

// Initialize lookup tables
(function initTables() {
  // Build cube edges - pairs of adjacent corners
  let k = 0;
  for (let i = 0; i < 8; ++i) {
    for (let j = 1; j <= 4; j <<= 1) {
      const p = i ^ j;
      if (i <= p) {
        CUBE_EDGES[k++] = i;
        CUBE_EDGES[k++] = p;
      }
    }
  }

  // Build edge table - which edges cross for each cube configuration
  for (let i = 0; i < 256; ++i) {
    let em = 0;
    for (let j = 0; j < 24; j += 2) {
      const a = !!(i & (1 << CUBE_EDGES[j]));
      const b = !!(i & (1 << CUBE_EDGES[j + 1]));
      em |= a !== b ? 1 << (j >> 1) : 0;
    }
    EDGE_TABLE[i] = em;
  }
})();

// ============== Helper Functions ==============

/**
 * Cached singleton empty output — avoids allocating zero-length arrays per call.
 * Consumers must not mutate these arrays.
 */
const EMPTY_OUTPUT: SurfaceNetOutput = Object.freeze({
  positions: new Float32Array(0),
  normals: new Float32Array(0),
  indices: new Uint32Array(0),
  materials: new Uint8Array(0),
  lights: new Float32Array(0),
  shoreFade: new Float32Array(0),
  cellIndices: new Uint16Array(0),
  boundaryFlags: new Uint8Array(0),
  vertexCount: 0,
  triangleCount: 0,
}) as SurfaceNetOutput;

// Weight to treat "other type" voxels as slightly outside surface
const FILTER_WEIGHT = -0.00001;

/**
 * Is this CELL in one of the grid's edge layers — the ones a cross-LOD seam reads?
 *
 * Cell 0 is the low face, 31 straddles the shared plane and 32 duplicates the neighbour's cell 0. A
 * cell in any of those layers on any axis must keep its vertex even when nothing draws it: the
 * mesher deliberately skips faces there (`ignoreFace`), so a boundary cell's vertex is computed and
 * then dropped by compaction — and it is exactly the vertex the seam has to weld to.
 */
export function isBoundaryCell(cell: number): boolean {
  const x = cell % GRID_SIZE;
  const y = ((cell / GRID_SIZE) | 0) % GRID_SIZE;
  const z = (cell / (GRID_SIZE * GRID_SIZE)) | 0;
  // Anchored to CHUNK_SIZE, not GRID: the straddling cell and the SHARED RING sit at CHUNK_SIZE-1
  // and CHUNK_SIZE whatever the margin is, and every ring past the plane has to be retained too or
  // the outermost one is compacted away before the seam can weld to it.
  const edge = (v: number): boolean => v === 0 || (v >= CHUNK_SIZE - 1 && v <= GRID_SIZE - 2);
  return edge(x) || edge(y) || edge(z);
}

/**
 * Is this voxel OUTSIDE the surface of `layer`? The mesher's own corner classification, exported so
 * anything that has to agree with it about where the surface is cannot drift from it.
 */
export function isOutsideForLayer(voxel: number, layer: number): boolean {
  const weight = ((voxel >> WEIGHT_SHIFT) & WEIGHT_MASK) * INV_WEIGHT_MAX_PACKED + WEIGHT_MIN;
  const matType = MATERIAL_TYPE_LUT[(voxel >> MATERIAL_SHIFT) & MATERIAL_MASK];
  return ((weight > 0 && matType !== layer) ? FILTER_WEIGHT : weight) < 0;
}

/** How many times a crossing into FILLED OPAQUE geometry counts when placing a vertex. */
const WRAP_WEIGHT = 3;

// Capped in world units, not cells: at lod 6 the per-cell burial dropped the drawn sea 7.7 m below
// itself. 2 fine voxels is above the largest lod-0 displacement, so lod 0 stays byte-identical.
const WRAP_BURY_FINE_VOXELS = 2;

// ============== Reusable Typed Array Pools ==============
// Pre-allocated buffers that grow as needed. Avoids per-chunk allocation.
// Each mesh type (solid/trans/liquid) gets its own set of buffers.

/** Initial capacity in vertices per mesh type */
const INITIAL_VERT_CAPACITY = 4096;
/** Initial capacity in triangles per mesh type */
const INITIAL_TRI_CAPACITY = 8192;

/** Pool of typed array buffers for each mesh type */
interface MeshPool {
  positions: Float32Array;   // 3 floats per vertex
  normals: Float32Array;     // 3 floats per vertex
  materials: Uint8Array;     // 1 byte per vertex
  lights: Float32Array;      // 1 float per vertex (sky light, 0.0-1.0)
  blockLights: Float32Array; // 1 float per vertex (block light, 0.0-1.0)
  shoreFade: Float32Array;   // 1 float per vertex (wrap share, 0.0-1.0)
  boundaryFlags: Uint8Array; // 1 byte per vertex (6-bit boundary-plane mask)
  cellIndices: Uint16Array;  // 1 uint16 per vertex (grid cell baseIdx for light re-sampling)
  indices: Uint32Array;      // 3 uints per triangle
  vertCapacity: number;
  triCapacity: number;
}

const meshPools: MeshPool[] = [];
for (let i = 0; i < MESH_COUNT; ++i) {
  meshPools.push({
    positions: new Float32Array(INITIAL_VERT_CAPACITY * 3),
    normals: new Float32Array(INITIAL_VERT_CAPACITY * 3),
    materials: new Uint8Array(INITIAL_VERT_CAPACITY),
    lights: new Float32Array(INITIAL_VERT_CAPACITY),
    blockLights: new Float32Array(INITIAL_VERT_CAPACITY),
    shoreFade: new Float32Array(INITIAL_VERT_CAPACITY),
    boundaryFlags: new Uint8Array(INITIAL_VERT_CAPACITY),
    cellIndices: new Uint16Array(INITIAL_VERT_CAPACITY),
    indices: new Uint32Array(INITIAL_TRI_CAPACITY * 3),
    vertCapacity: INITIAL_VERT_CAPACITY,
    triCapacity: INITIAL_TRI_CAPACITY,
  });
}

/**
 * Per-mesh-type grow-only buffers for normalized normals.
 * Avoids allocating a new Float32Array in buildFinalOutput() every call.
 */
const normalizedNormalBuffers: Float32Array[] = [
  new Float32Array(INITIAL_VERT_CAPACITY * 3),
  new Float32Array(INITIAL_VERT_CAPACITY * 3),
  new Float32Array(INITIAL_VERT_CAPACITY * 3),
];
const normalizedNormalCapacities = [INITIAL_VERT_CAPACITY, INITIAL_VERT_CAPACITY, INITIAL_VERT_CAPACITY];

/** Ensure pool has enough vertex capacity, growing by 2x if needed */
function ensureVertCapacity(pool: MeshPool, needed: number): void {
  if (needed <= pool.vertCapacity) return;
  let cap = pool.vertCapacity;
  while (cap < needed) cap <<= 1;
  const newPos = new Float32Array(cap * 3);
  newPos.set(pool.positions);
  pool.positions = newPos;
  const newNorm = new Float32Array(cap * 3);
  newNorm.set(pool.normals);
  pool.normals = newNorm;
  const newMat = new Uint8Array(cap);
  newMat.set(pool.materials);
  pool.materials = newMat;
  const newLights = new Float32Array(cap);
  newLights.set(pool.lights);
  pool.lights = newLights;
  const newBlockLights = new Float32Array(cap);
  newBlockLights.set(pool.blockLights);
  pool.blockLights = newBlockLights;
  const newShoreFade = new Float32Array(cap);
  newShoreFade.set(pool.shoreFade);
  pool.shoreFade = newShoreFade;
  const newBoundary = new Uint8Array(cap);
  newBoundary.set(pool.boundaryFlags);
  pool.boundaryFlags = newBoundary;
  const newCellIndices = new Uint16Array(cap);
  newCellIndices.set(pool.cellIndices);
  pool.cellIndices = newCellIndices;
  pool.vertCapacity = cap;
}

/** Ensure pool has enough triangle capacity, growing by 2x if needed */
function ensureTriCapacity(pool: MeshPool, needed: number): void {
  if (needed <= pool.triCapacity) return;
  let cap = pool.triCapacity;
  while (cap < needed) cap <<= 1;
  const newIdx = new Uint32Array(cap * 3);
  newIdx.set(pool.indices);
  pool.indices = newIdx;
  pool.triCapacity = cap;
}

// ============== Pre-computed Grid State (fixed for the GRID_SIZE³ grid) ==============
// These are derived from GRID_SIZE which never changes at runtime.
// Hoisted to module level to avoid re-allocation on every meshVoxelsSplit() call.

/**
 * Vertex index buffers for connecting faces (one per mesh type).
 *
 * A slot holds NO_VERTEX until the cell that owns it emits a vertex. That is not defensive
 * padding — it is the buffer's contract, and the reason is S2-1's NaN/monster-triangle defect:
 *
 * SurfaceNets emits a quad per crossing EDGE, joining the vertices of the four cells around it,
 * which it reads out of this buffer. That is only sound under an invariant — if an edge crosses in
 * one cell it crosses in all four, so all four emitted a vertex. It holds whenever a corner's
 * inside/outside test is a pure function of the CORNER. Phase 2 broke it for liquid by conditioning
 * that test on the cell, and the buffers, being module-level and never cleared, then returned a STALE
 * index from whatever chunk was meshed before: inside the current vertex range it drew a triangle
 * across the whole chunk — liquid triangles to 10752 m², 336× a voxel triangle, at the sea surface —
 * and past it `expandGeometry` read off the end of the positions subarray and wrote
 * `undefined * scale`, i.e. 327 NaN-position triangles in 12 named chunks (probe evidence, commit
 * cf8f1cb). Both are one bug with two ranges of the same garbage index.
 *
 * All three layers classify per corner again, so the invariant holds by construction and this can no
 * longer fire. It stays because it is cheap and because it states the contract: every visited cell
 * writes its slot, and a face is built only when all four of its cells produced one.
 */
const VERT_IDX_BUFFER_SIZE = (GRID_SIZE + 1) * (GRID_SIZE + 1) * 2;
const NO_VERTEX = -1;
const vertIdxBuffers = [
  new Int32Array(VERT_IDX_BUFFER_SIZE).fill(NO_VERTEX),
  new Int32Array(VERT_IDX_BUFFER_SIZE).fill(NO_VERTEX),
  new Int32Array(VERT_IDX_BUFFER_SIZE).fill(NO_VERTEX),
];

/**
 * Vertex remap for compaction (grow-only, one per mesh type is unnecessary — it is consumed
 * immediately per layer in buildFinalOutput).
 */
let vertRemap = new Int32Array(INITIAL_VERT_CAPACITY);

/** Per-mesh-type write cursors */
const vertCounts = new Int32Array(MESH_COUNT);
const triCounts = new Int32Array(MESH_COUNT);

/** SurfaceNet corner weight grids per mesh type */
const grids = [new Float32Array(8), new Float32Array(8), new Float32Array(8)];

/**
 * Per-cell corner scratch — the 8 corners' raw weights and material types, gathered ONCE per
 * cell before any per-mesh-type pass runs. Module-level and preallocated: this is the mesher's
 * hottest loop, so nothing here may allocate (perf invariants, docs/terrain-generation-performance.md).
 *
 * The gather is separate from the classification because the FACE LOOP needs the corners' raw
 * weights and material types after the fact: which faces are hidden behind opaque geometry is a
 * property of the crossing edge's outside corner, and that answer cannot be read off the adjusted
 * per-layer grids.
 *
 * cornerW is a Float64Array, NOT Float32Array, on purpose: the weight is computed as a double and
 * the max-weight comparisons below are double-vs-float32. Round-tripping it through a Float32Array
 * here would perturb those ties and move the solid/transparent output (F17 hashes).
 */
const cornerW = new Float64Array(8);
const cornerT = new Uint8Array(8);

/** Per-cell state for each mesh type (reused each cell) */
const masks = new Uint8Array(MESH_COUNT);
const maxIdxs = new Int32Array(MESH_COUNT);
const maxWeights = new Float32Array(MESH_COUNT);

/** Grid traversal position */
const xPos = new Int32Array(3);

/** The SHARED RING's cell index — the high face. At CHUNK_SIZE regardless of margin. */
const highBoundary = new Int32Array([CHUNK_SIZE, CHUNK_SIZE, CHUNK_SIZE]);

/** 2x2x2 corner offsets into flat grid array — derived from the grid strides. */
const cornerOffsets = new Int32Array([
  0,                                          // (0,0,0)
  1,                                          // (1,0,0)
  GRID_STRIDE_X,                              // (0,1,0)
  GRID_STRIDE_X + 1,                          // (1,1,0)
  GRID_STRIDE_XY,                             // (0,0,1)
  GRID_STRIDE_XY + 1,                         // (1,0,1)
  GRID_STRIDE_XY + GRID_STRIDE_X,             // (0,1,1)
  GRID_STRIDE_XY + GRID_STRIDE_X + 1,         // (1,1,1)
]);

/**
 * Generate meshes from voxel data using SurfaceNets algorithm.
 * 
 * SINGLE-PASS with MULTI-SURFACE: Traverses voxel grid once but generates
 * separate meshes for solid, transparent, and liquid materials.
 * 
 * Key insight: At material type boundaries, we need surfaces on BOTH sides.
 * This is achieved by treating "other type" voxels as air when computing each mask.
 * 
 * PERFORMANCE OPTIMIZATIONS:
 * - Pre-allocated typed array pools eliminate ~27K JS object allocations per chunk
 * - Inlined bit operations avoid ~314K function calls per chunk
 * - Write directly to Float32Array/Uint32Array, no buildOutput() copy step
 * - Normal accumulation uses flat Float32Array with 3-float stride
 * 
 * @param input Flat voxel data array and dimensions
 * @returns Split mesh outputs for solid, transparent, and liquid materials
 */
export function meshVoxelsSplit(input: SurfaceNetInput): SplitSurfaceNetOutput {
  const { dims, data, skipHighBoundary = [false, false, false] } = input;
  const wrapBuryCells = WRAP_BURY_FINE_VOXELS / (1 << (input.lod ?? 0));
  
  // Grid strides (constant for the GRID_SIZE³ grid, but kept as locals for JIT hinting)
  const dimsX = dims[0];
  const dimsXY = dims[0] * dims[1];

  // === Early bail — use shared utility that respects bit layout constants ===
  if (!hasSurfaceCrossing(data)) {
    return { solid: EMPTY_OUTPUT, transparent: EMPTY_OUTPUT, liquid: EMPTY_OUTPUT };
  }

  // Reset write cursors (pools/buffers are module-level, no allocation needed)
  vertCounts[0] = vertCounts[1] = vertCounts[2] = 0;
  triCounts[0] = triCounts[1] = triCounts[2] = 0;
  
  // Grid traversal state
  let n = 0;
  const x = xPos;
  x[0] = x[1] = x[2] = 0;
  const R = new Int32Array([1, dimsX + 1, (dimsX + 1) * (dims[1] + 1)]);
  
  let bufNo = 1;

  // March over the voxel grid
  for (x[2] = 0; x[2] < dims[2] - 1; ++x[2], n += dims[0], bufNo ^= 1, R[2] = -R[2]) {
    let m = 1 + (dims[0] + 1) * (1 + bufNo * (dims[1] + 1));

    for (x[1] = 0; x[1] < dims[1] - 1; ++x[1], ++n, m += 2) {
      for (x[0] = 0; x[0] < dims[0] - 1; ++x[0], ++n, ++m) {
        // Base index in flat array for current cell position
        const baseIdx = x[2] * dimsXY + x[1] * dimsX + x[0];
        
        // Reset per-mesh state
        masks[0] = masks[1] = masks[2] = 0;
        maxWeights[0] = maxWeights[1] = maxWeights[2] = -Infinity;
        maxIdxs[0] = maxIdxs[1] = maxIdxs[2] = baseIdx;
        let maxLight = 0;
        let maxBlock = 0;
        /** Bit g set = corner g is a FILLED OPAQUE voxel: it hides faces, and it pulls edges. */
        let opaqueCorners = 0;

        // === Pass A: gather the 2x2x2 corners ONCE into module scratch ===
        // Bit operations use shared constants so changes to voxel layout propagate.
        for (let g = 0; g < 8; ++g) {
          const voxel = data[baseIdx + cornerOffsets[g]];
          const weight = ((voxel >> WEIGHT_SHIFT) & WEIGHT_MASK) * INV_WEIGHT_MAX_PACKED + WEIGHT_MIN;
          const material = (voxel >> MATERIAL_SHIFT) & MATERIAL_MASK;
          const matType = MATERIAL_TYPE_LUT[material]; // 0=solid, 1=trans, 2=liquid
          cornerW[g] = weight;
          cornerT[g] = matType;
          if (weight > 0 && matType === MAT_TYPE_SOLID) opaqueCorners |= 1 << g;

          // Track max light from non-opaque corners for vertex light.
          // Include air (weight < 0) AND liquid/transparent voxels (weight > 0
          // but matType != solid) so terrain under water isn't pitch black.
          if (weight < 0 || matType !== MAT_TYPE_SOLID) {
            const light = voxel & LIGHT_MASK;
            if (light > maxLight) maxLight = light;
            const blockLight = (voxel >>> BLOCK_LIGHT_SHIFT) & BLOCK_LIGHT_MASK;
            if (blockLight > maxBlock) maxBlock = blockLight;
          }
        }

        // === Pass B: ONE classification for all three layers ===
        // Own material keeps its weight; any OTHER filled material is just-outside; air keeps its
        // real weight. So each layer meshes the whole boundary of its own material — the free surface
        // against air AND the interface against other materials — and because FILTER_WEIGHT sits a
        // hair below zero, a crossing against a filled neighbour lands essentially ON that corner.
        // That is what makes a layer's edge WRAP onto the material beside it instead of being cut off
        // at the cell lattice, and it is the whole of the transparent layer's organic look.
        //
        // WHICH OF THOSE FACES GET DRAWN IS DECIDED PER EDGE, IN THE FACE LOOP BELOW, not here. Phase
        // 2 removed the water underside by making this classification conditional on the CELL
        // (`cellHasLiquid`), which is a per-cell answer to a per-face question: it broke SurfaceNets'
        // rule that a corner's side is a pure function of the corner, and that break is what produced
        // stale vertex indices, NaN triangles and 10752 m² monster triangles (S2-1), then needed a
        // dilated ring to close the shoreline it had opened. The underside is now suppressed where it
        // belongs — on the edge that would draw it — and both traps phase 2 was avoiding are
        // impossible here: rock is OUTSIDE for the liquid layer, so a dry rock/air cell has no liquid
        // crossing at all, and rock is pinned to FILTER_WEIGHT, so it can never win the material vote
        // against water's positive weight.
        for (let mt = 0; mt < MESH_COUNT; ++mt) {
          const gridMt = grids[mt];
          for (let g = 0; g < 8; ++g) {
            const w = cornerW[g];
            const adjustedWeight = (w > 0 && cornerT[g] !== mt) ? FILTER_WEIGHT : w;
            gridMt[g] = adjustedWeight;
            masks[mt] |= adjustedWeight < 0 ? 1 << g : 0;
            if (adjustedWeight > maxWeights[mt]) {
              maxWeights[mt] = adjustedWeight;
              maxIdxs[mt] = baseIdx + cornerOffsets[g];
            }
          }
        }

        // Check boundary conditions
        const onLowBoundary = (x[0] === 0 || x[1] === 0 || x[2] === 0);
        const onHighBoundaryNoNeighbor = 
          (skipHighBoundary[0] && x[0] >= highBoundary[0] - 1) ||
          (skipHighBoundary[1] && x[1] >= highBoundary[1] - 1) ||
          (skipHighBoundary[2] && x[2] >= highBoundary[2] - 1);
        const ignoreFace = onLowBoundary || onHighBoundaryNoNeighbor;

        // Process each mesh type
        for (let mt = 0; mt < MESH_COUNT; ++mt) {
          const mask = masks[mt];
          const buffer = vertIdxBuffers[mt];
          // No crossing in this cell → no vertex, and the slot must say so (see NO_VERTEX above).
          if (mask === 0 || mask === 0xff) { buffer[m] = NO_VERTEX; continue; }

          const grid = grids[mt];
          const pool = meshPools[mt];
          
          const edgeMask = EDGE_TABLE[mask];
          
          // Compute vertex position (inline, no tuple allocation)
          let vx = 0, vy = 0, vz = 0;
          let eCount = 0;
          /** Of the weight in that average, how much came from crossings into opaque geometry. */
          let wrapSum = 0;
          /** The same average over the DRAWN crossings only, plus their outward normal. */
          let dvy = 0, dvx = 0, dvz = 0, dCount = 0;
          let nx = 0, ny = 0, nz = 0;
          
          for (let ei = 0; ei < 12; ++ei) {
            if (!(edgeMask & (1 << ei))) continue;

            const e0 = CUBE_EDGES[ei << 1];
            const e1 = CUBE_EDGES[(ei << 1) + 1];
            const g0 = grid[e0];
            const g1 = grid[e1];
            let t = g0 - g1;

            if (Math.abs(t) > 1e-6) {
              t = g0 / t;
            } else {
              ++eCount;
              continue;
            }

            // A crossing against a FILLED OPAQUE corner is the layer's edge running into the ground,
            // and it counts WRAP_WEIGHT times in the average, which walks the vertex the rest of the
            // way onto that surface. Plain averaging leaves it short: a sheet clipped by a slope keeps
            // most of its crossings on the free surface, so the rim stopped 0.113 voxels outside the
            // solid — a hairline of undrawn water along every shoreline, 0.9 m of it at lod 5.
            //
            // It is a weight on a CROSSING, not a direction: whichever way the two surfaces face, the
            // edge is pulled along its own normal. For MESH_SOLID it never applies — an outside corner
            // there is air or a filled non-solid material, never filled opaque — so terrain is
            // untouched, and the layer that draws a surface against opaque geometry keeps placing it
            // exactly where it always did.
            const outC = g0 < 0 ? e0 : e1;
            const wgt = (opaqueCorners >> outC) & 1 ? WRAP_WEIGHT : 1;
            eCount += wgt;
            if (wgt !== 1) wrapSum += wgt;
            else {
              dCount++;
              const ax = e0 ^ e1;
              const sgn = (outC & ax) ? 1 : -1;
              if (ax & 1) nx += sgn; else if (ax & 2) ny += sgn; else nz += sgn;
            }

            // Axis 0 (X)
            {
              const a = e0 & 1;
              const b = e1 & 1;
              const c = a !== b ? (a ? 1.0 - t : t) : (a ? 1 : 0);
              vx += wgt * c;
              if (wgt === 1) dvx += c;
            }
            // Axis 1 (Y)
            {
              const a = e0 & 2;
              const b = e1 & 2;
              const c = a !== b ? (a ? 1.0 - t : t) : (a ? 1 : 0);
              vy += wgt * c;
              if (wgt === 1) dvy += c;
            }
            // Axis 2 (Z)
            {
              const a = e0 & 4;
              const b = e1 & 4;
              const c = a !== b ? (a ? 1.0 - t : t) : (a ? 1 : 0);
              vz += wgt * c;
              if (wgt === 1) dvz += c;
            }
          }

          if (eCount > 0) {
            const s = 1.0 / eCount;
            let px = x[0] + s * vx;
            let py = x[1] + s * vy;
            let pz = x[2] + s * vz;

            // Cap the wrap's offset ACROSS the sheet only; along it the rim still walks onto the
            // solid beside it. MESH_SOLID never reaches this (no filled-opaque outside corner).
            const nn = nx * nx + ny * ny + nz * nz;
            if (mt === MESH_LIQUID && wrapSum > 0 && dCount > 0 && nn > 0) {
              const nl = Math.sqrt(nn);
              const sd = 1.0 / dCount;
              const off = ((px - x[0] - sd * dvx) * nx + (py - x[1] - sd * dvy) * ny
                + (pz - x[2] - sd * dvz) * nz) / nl;
              const want = off > wrapBuryCells ? wrapBuryCells
                : off < -wrapBuryCells ? -wrapBuryCells : off;
              if (want !== off) {
                const d = (want - off) / nl;
                px = Math.min(x[0] + 1, Math.max(x[0], px + d * nx));
                py = Math.min(x[1] + 1, Math.max(x[1], py + d * ny));
                pz = Math.min(x[2] + 1, Math.max(x[2], pz + d * nz));
              }
            }
            
            // === 3a: Write directly to typed array pool ===
            const vertIdx = vertCounts[mt];
            ensureVertCapacity(pool, vertIdx + 1);
            buffer[m] = vertIdx;
            
            const v3 = vertIdx * 3;
            pool.positions[v3] = px;
            pool.positions[v3 + 1] = py;
            pool.positions[v3 + 2] = pz;
            // Initialize normal accumulator to zero
            pool.normals[v3] = 0;
            pool.normals[v3 + 1] = 0;
            pool.normals[v3 + 2] = 0;
            // Extract material using shared constants
            pool.materials[vertIdx] = (data[maxIdxs[mt]] >> MATERIAL_SHIFT) & MATERIAL_MASK;
            // Light: max of non-solid corners, computed in the corner loop above
            pool.lights[vertIdx] = maxLight / LIGHT_MAX;
            pool.blockLights[vertIdx] = maxBlock / BLOCK_LIGHT_MAX;
            // Wrap share, for the shaders to pin a welded rim against (see SurfaceNetOutput).
            pool.shoreFade[vertIdx] = wrapSum / eCount;
            // Boundary-plane mask: which of the 6 chunk-cell faces this vertex sits on.
            // Cell coords run 0..32; low face = 0, high face = highBoundary (32).
            pool.boundaryFlags[vertIdx] =
              (x[0] === 0 ? 1 : 0) |
              (x[0] === highBoundary[0] ? 2 : 0) |
              (x[1] === 0 ? 4 : 0) |
              (x[1] === highBoundary[1] ? 8 : 0) |
              (x[2] === 0 ? 16 : 0) |
              (x[2] === highBoundary[2] ? 32 : 0);
            // Cell origin index for light re-sampling (same baseIdx the corner loop sampled).
            pool.cellIndices[vertIdx] = baseIdx;
            vertCounts[mt] = vertIdx + 1;
            
            // Generate faces
            for (let fi = 0; fi < 3; ++fi) {
              if (!(edgeMask & (1 << fi))) continue;
              
              const iu = (fi + 1) % 3;
              const iv = (fi + 2) % 3;
              
              if (x[iu] === 0 || x[iv] === 0) continue;
              
              const du = R[iu];
              const dv = R[iv];
              
              const idx0 = buffer[m];
              const idx1 = buffer[m - du];
              const idx2 = buffer[m - dv];
              const idx3 = buffer[m - du - dv];

              // A face needs all four of its cells to have produced a vertex IN THIS MESH TYPE.
              // Guaranteed by the algorithm now that every layer classifies per corner; kept as the
              // contract of the index buffer (see NO_VERTEX above).
              if (idx1 < 0 || idx2 < 0 || idx3 < 0) continue;

              // === IS THIS FACE BURIED BEHIND OPAQUE GEOMETRY? ===
              // Edges 0..2 run from corner 0 to corner 1<<fi (see CUBE_EDGES), and `mask & 1` says
              // which end is outside — so the outside corner of this crossing is one of those two.
              // If that corner is a FILLED OPAQUE voxel, the surface we would draw here is inside
              // the solid's own volume: the water/rock interface that used to mesh the seabed a
              // second time in water material (56% of all liquid triangles, stacking alpha over the
              // whole sea), and the 43% of a painted transparent layer that sits inside the wall.
              //
              // Deciding it HERE, per edge, is what makes it safe: the answer depends only on the
              // edge's two corner voxels, so all four cells around the edge agree by construction
              // and no crack can open between them. The face's NORMALS are still accumulated below —
              // the drawn boundary keeps the shading of the wrap it is cut from, which is why the
              // edge reads as organic rather than as a hard rim.
              //
              // For MESH_SOLID this can never fire: an outside corner there is air or a filled
              // non-solid material, never filled opaque, so the solid layer is untouched.
              const outG = (mask & 1) ? 0 : (1 << fi);
              const hidden = ((opaqueCorners >> outG) & 1) !== 0;

              // Read vertex positions from typed arrays for normal computation
              const i0x3 = idx0 * 3, i1x3 = idx1 * 3, i2x3 = idx2 * 3, i3x3 = idx3 * 3;
              const pos = pool.positions;
              const nrm = pool.normals;
              
              // Compute face normals inline
              let e0x: number, e0y: number, e0z: number;
              let e1x: number, e1y: number, e1z: number;
              let nX: number, nY: number, nZ: number, lenSq: number, len: number;
              
              if (mask & 1) {
                // Face 1: idx1, idx0, idx3
                e0x = pos[i1x3] - pos[i0x3]; e0y = pos[i1x3+1] - pos[i0x3+1]; e0z = pos[i1x3+2] - pos[i0x3+2];
                e1x = pos[i3x3] - pos[i1x3]; e1y = pos[i3x3+1] - pos[i1x3+1]; e1z = pos[i3x3+2] - pos[i1x3+2];
                nX = e0y * e1z - e0z * e1y;
                nY = e0z * e1x - e0x * e1z;
                nZ = e0x * e1y - e0y * e1x;
                lenSq = nX * nX + nY * nY + nZ * nZ;
                if (lenSq < 0.000001) { nX = 0; nY = 1; nZ = 0; }
                else { len = 1.0 / Math.sqrt(lenSq); nX *= len; nY *= len; nZ *= len; }
                
                nrm[i1x3] += nX; nrm[i1x3+1] += nY; nrm[i1x3+2] += nZ;
                nrm[i0x3] += nX; nrm[i0x3+1] += nY; nrm[i0x3+2] += nZ;
                nrm[i3x3] += nX; nrm[i3x3+1] += nY; nrm[i3x3+2] += nZ;
                
                // Face 2: idx2, idx3, idx0
                e0x = pos[i2x3] - pos[i3x3]; e0y = pos[i2x3+1] - pos[i3x3+1]; e0z = pos[i2x3+2] - pos[i3x3+2];
                e1x = pos[i0x3] - pos[i2x3]; e1y = pos[i0x3+1] - pos[i2x3+1]; e1z = pos[i0x3+2] - pos[i2x3+2];
                nX = e0y * e1z - e0z * e1y;
                nY = e0z * e1x - e0x * e1z;
                nZ = e0x * e1y - e0y * e1x;
                lenSq = nX * nX + nY * nY + nZ * nZ;
                if (lenSq < 0.000001) { nX = 0; nY = 1; nZ = 0; }
                else { len = 1.0 / Math.sqrt(lenSq); nX *= len; nY *= len; nZ *= len; }
                
                nrm[i2x3] += nX; nrm[i2x3+1] += nY; nrm[i2x3+2] += nZ;
                nrm[i3x3] += nX; nrm[i3x3+1] += nY; nrm[i3x3+2] += nZ;
                nrm[i0x3] += nX; nrm[i0x3+1] += nY; nrm[i0x3+2] += nZ;
                
                if (!ignoreFace && !hidden) {
                  ensureTriCapacity(pool, triCounts[mt] + 2);
                  const ti = triCounts[mt] * 3;
                  pool.indices[ti] = idx1; pool.indices[ti+1] = idx0; pool.indices[ti+2] = idx3;
                  pool.indices[ti+3] = idx2; pool.indices[ti+4] = idx3; pool.indices[ti+5] = idx0;
                  triCounts[mt] += 2;
                }
              } else {
                // Face 1: idx2, idx0, idx3
                e0x = pos[i2x3] - pos[i0x3]; e0y = pos[i2x3+1] - pos[i0x3+1]; e0z = pos[i2x3+2] - pos[i0x3+2];
                e1x = pos[i3x3] - pos[i2x3]; e1y = pos[i3x3+1] - pos[i2x3+1]; e1z = pos[i3x3+2] - pos[i2x3+2];
                nX = e0y * e1z - e0z * e1y;
                nY = e0z * e1x - e0x * e1z;
                nZ = e0x * e1y - e0y * e1x;
                lenSq = nX * nX + nY * nY + nZ * nZ;
                if (lenSq < 0.000001) { nX = 0; nY = 1; nZ = 0; }
                else { len = 1.0 / Math.sqrt(lenSq); nX *= len; nY *= len; nZ *= len; }
                
                nrm[i2x3] += nX; nrm[i2x3+1] += nY; nrm[i2x3+2] += nZ;
                nrm[i0x3] += nX; nrm[i0x3+1] += nY; nrm[i0x3+2] += nZ;
                nrm[i3x3] += nX; nrm[i3x3+1] += nY; nrm[i3x3+2] += nZ;
                
                // Face 2: idx1, idx3, idx0
                e0x = pos[i1x3] - pos[i3x3]; e0y = pos[i1x3+1] - pos[i3x3+1]; e0z = pos[i1x3+2] - pos[i3x3+2];
                e1x = pos[i0x3] - pos[i1x3]; e1y = pos[i0x3+1] - pos[i1x3+1]; e1z = pos[i0x3+2] - pos[i1x3+2];
                nX = e0y * e1z - e0z * e1y;
                nY = e0z * e1x - e0x * e1z;
                nZ = e0x * e1y - e0y * e1x;
                lenSq = nX * nX + nY * nY + nZ * nZ;
                if (lenSq < 0.000001) { nX = 0; nY = 1; nZ = 0; }
                else { len = 1.0 / Math.sqrt(lenSq); nX *= len; nY *= len; nZ *= len; }
                
                nrm[i1x3] += nX; nrm[i1x3+1] += nY; nrm[i1x3+2] += nZ;
                nrm[i3x3] += nX; nrm[i3x3+1] += nY; nrm[i3x3+2] += nZ;
                nrm[i0x3] += nX; nrm[i0x3+1] += nY; nrm[i0x3+2] += nZ;
                
                if (!ignoreFace && !hidden) {
                  ensureTriCapacity(pool, triCounts[mt] + 2);
                  const ti = triCounts[mt] * 3;
                  pool.indices[ti] = idx2; pool.indices[ti+1] = idx0; pool.indices[ti+2] = idx3;
                  pool.indices[ti+3] = idx1; pool.indices[ti+4] = idx3; pool.indices[ti+5] = idx0;
                  triCounts[mt] += 2;
                }
              }
            }
          } else {
            // A mask that says "crossing" but no usable edge — the cell contributes no vertex, so
            // its slot must not keep an older chunk's index either.
            buffer[m] = NO_VERTEX;
          }
        }
      }
    }
  }

  /** Drop vertices no triangle uses, remapping the index buffer. Returns the surviving count. */
  function compactVertices(mt: number, tc: number): number {
    const pool = meshPools[mt];
    const vc = vertCounts[mt];
    const idx = pool.indices;
    const iEnd = tc * 3;
    if (vertRemap.length < vc) vertRemap = new Int32Array(vc);
    const remap = vertRemap;
    remap.fill(-1, 0, vc);
    for (let i = 0; i < iEnd; ++i) remap[idx[i]] = 0;
    // ...and every vertex in an edge-layer CELL, drawn or not. See isBoundaryCell.
    for (let v = 0; v < vc; ++v) {
      if (remap[v] !== 0 && isBoundaryCell(pool.cellIndices[v])) remap[v] = 0;
    }

    let kept = 0;
    for (let v = 0; v < vc; ++v) {
      if (remap[v] !== 0) continue;
      remap[v] = kept;
      if (kept !== v) {
        const d = kept * 3, s3 = v * 3;
        pool.positions[d] = pool.positions[s3];
        pool.positions[d + 1] = pool.positions[s3 + 1];
        pool.positions[d + 2] = pool.positions[s3 + 2];
        pool.normals[d] = pool.normals[s3];
        pool.normals[d + 1] = pool.normals[s3 + 1];
        pool.normals[d + 2] = pool.normals[s3 + 2];
        pool.materials[kept] = pool.materials[v];
        pool.lights[kept] = pool.lights[v];
        pool.blockLights[kept] = pool.blockLights[v];
        pool.shoreFade[kept] = pool.shoreFade[v];
        pool.boundaryFlags[kept] = pool.boundaryFlags[v];
        pool.cellIndices[kept] = pool.cellIndices[v];
      }
      ++kept;
    }
    if (kept !== vc) for (let i = 0; i < iEnd; ++i) idx[i] = remap[idx[i]];
    return kept;
  }

  // === Build final outputs using subarray views (zero-copy) ===
  // IMPORTANT: Returned views are only valid until the next meshVoxelsSplit() call.
  // Callers must consume the data synchronously before meshing another chunk.
  function buildFinalOutput(mt: number): SurfaceNetOutput {
    const tc = triCounts[mt];
    const pool = meshPools[mt];
    // A layer's cells emit a vertex wherever its material has a boundary, but faces buried behind
    // opaque geometry are not drawn (see the face loop) — so a submerged seabed, or the inside of a
    // painted wall, leaves vertices no triangle references. Dropping them keeps the uploaded buffers
    // to what is actually drawn; it is a linear pass, and the arrays compact in place because the
    // write cursor never overtakes the read cursor.
    //
    // NOT FOR MESH_SOLID. Suppression cannot fire there (an outside corner is air or a filled
    // non-solid material, never filled opaque), so the solid layer has no vertices to drop that this
    // change created — only the ones `ignoreFace` has always left, at a chunk's low boundary. Leaving
    // them alone is what keeps the solid output byte-identical, which is the F17 promise.
    // A layer that draws nothing still holds the border vertices a cross-LOD seam welds to.
    const vc = tc === 0 ? compactVertices(mt, 0)
      : mt === MESH_SOLID ? vertCounts[mt] : compactVertices(mt, tc);
    if (vc === 0) return EMPTY_OUTPUT;

    // Grow normalized normals buffer if needed
    if (vc > normalizedNormalCapacities[mt]) {
      let cap = normalizedNormalCapacities[mt];
      while (cap < vc) cap <<= 1;
      normalizedNormalBuffers[mt] = new Float32Array(cap * 3);
      normalizedNormalCapacities[mt] = cap;
    }
    
    // Normalize accumulated normals and negate (face normals point inward from winding)
    const normSrc = pool.normals;
    const normals = normalizedNormalBuffers[mt];
    for (let i = 0; i < vc; ++i) {
      const i3 = i * 3;
      const nx = normSrc[i3];
      const ny = normSrc[i3 + 1];
      const nz = normSrc[i3 + 2];
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
      if (len > 0.0001) {
        const invLen = -1.0 / len;
        normals[i3] = nx * invLen;
        normals[i3 + 1] = ny * invLen;
        normals[i3 + 2] = nz * invLen;
      } else {
        normals[i3] = 0;
        normals[i3 + 1] = 1;
        normals[i3 + 2] = 0;
      }
    }
    
    // Return subarray views — zero allocation, valid until next meshVoxelsSplit()
    return {
      positions: pool.positions.subarray(0, vc * 3),
      normals: normals.subarray(0, vc * 3),
      indices: pool.indices.subarray(0, tc * 3),
      materials: pool.materials.subarray(0, vc),
      lights: pool.lights.subarray(0, vc),
      blockLights: pool.blockLights.subarray(0, vc),
      shoreFade: pool.shoreFade.subarray(0, vc),
      cellIndices: pool.cellIndices.subarray(0, vc),
      boundaryFlags: pool.boundaryFlags.subarray(0, vc),
      vertexCount: vc,
      triangleCount: tc,
    };
  }

  return { 
    solid: buildFinalOutput(MESH_SOLID), 
    transparent: buildFinalOutput(MESH_TRANS), 
    liquid: buildFinalOutput(MESH_LIQUID) 
  };
}

/**
 * Check if a SurfaceNet output is empty (no geometry).
 */
export function isEmptyMesh(output: SurfaceNetOutput): boolean {
  return output.vertexCount === 0 || output.triangleCount === 0;
}

/**
 * Sample a cell's vertex light from an expanded GRID_SIZE³ grid, returning both channels packed
 * into one int: `maxSky | (maxBlock << 5)` (each 0..31). This is the LIGHT-ONLY companion
 * to the meshing hot loop above — it MUST stay bit-identical to the light computation at
 * `meshVoxelsSplit` (the `maxLight`/`maxBlock` corner loop + the `pool.lights` write), so a
 * re-sample of an unchanged mesh reproduces exactly the light the worker would have baked.
 *
 * Used by the light-only relight path (ChunkGeometry.resampleLightFromGrid): when a chunk's
 * voxels are unchanged but neighbour light changed, we rebuild its GRID_SIZE³ grid and re-read each
 * stored cell instead of re-running SurfaceNets over it.
 *
 * @param grid     Expanded GRID_SIZE³ voxel grid (same layout meshVoxelsSplit consumes).
 * @param baseIdx  Cell origin index (the `cellIndices` value stored per expanded vertex).
 */
export function sampleCellLight(grid: Uint32Array, baseIdx: number): number {
  let maxLight = 0;
  let maxBlock = 0;
  for (let g = 0; g < 8; ++g) {
    const voxel = grid[baseIdx + cornerOffsets[g]];
    const weight = ((voxel >> WEIGHT_SHIFT) & WEIGHT_MASK) * INV_WEIGHT_MAX_PACKED + WEIGHT_MIN;
    const material = (voxel >> MATERIAL_SHIFT) & MATERIAL_MASK;
    // Same non-opaque test as the mesh loop: air OR non-solid (liquid/transparent) corners.
    if (weight < 0 || MATERIAL_TYPE_LUT[material] !== MAT_TYPE_SOLID) {
      const light = voxel & LIGHT_MASK;
      if (light > maxLight) maxLight = light;
      const blockLight = (voxel >>> BLOCK_LIGHT_SHIFT) & BLOCK_LIGHT_MASK;
      if (blockLight > maxBlock) maxBlock = blockLight;
    }
  }
  return maxLight | (maxBlock << 5);
}

/** Normalized sky-light channel from a packed sampleCellLight() result (0.0–1.0). */
export function unpackSkyLight(packed: number): number {
  return (packed & 0x1f) / LIGHT_MAX;
}

/** Normalized block-light channel from a packed sampleCellLight() result (0.0–1.0). */
export function unpackBlockLight(packed: number): number {
  return ((packed >>> 5) & 0x1f) / BLOCK_LIGHT_MAX;
}
